import { UserGuideCategory } from '../types/userGuide.types';

export const userGuideCategories: UserGuideCategory[] = [
  {
    id: 'getting-started',
    name: 'Getting Started',
    icon: 'Rocket',
    description: 'Learn the basics and set up your FutureLens Pro account',
    orderIndex: 1,
    articles: [
      {
        id: 'welcome',
        slug: 'welcome-to-futurelens',
        title: 'Welcome to FutureLens Pro',
        category: 'getting-started',
        difficulty: 'beginner',
        estimatedReadTime: 5,
        orderIndex: 1,
        description: 'Overview of FutureLens Pro features and capabilities',
        content: `# Welcome to FutureLens Pro

FutureLens Pro combines multiple powerful tools to accelerate your career growth. Here's what you can accomplish:

## Core Features Overview

### Career Intelligence Reports
- AI-powered analysis of your complete career profile
- Comprehensive skills assessment and value proposition
- Strategic recommendations for advancement
- Exportable PDF reports for interviews and planning

### Skills Tracker
- Track your skill development journey
- Set priorities and milestones
- Access curated learning resources
- Visualize progress with analytics
- Calculate ROI for skill investments

### AI Coach
- Personalized career coaching powered by AI
- Voice-enabled conversations
- Context-aware recommendations
- Multiple coaching personas for different needs
- Conversation history and management

### Job Match AI
- Upload job descriptions for instant analysis
- Get detailed compatibility scores
- Receive executive interview strategies
- Identify skill gaps with development plans
- Access AI-powered interview preparation`
      },
      {
        id: 'first-30-days',
        slug: 'your-first-30-days',
        title: 'Your First 30 Days',
        category: 'getting-started',
        difficulty: 'beginner',
        estimatedReadTime: 8,
        orderIndex: 2,
        description: 'A step-by-step guide to getting the most value in your first month',
        content: `# Your First 30 Days with FutureLens Pro

## Week 1: Foundation
- Complete your user profile (10 minutes)
- Upload your resume (5 minutes)
- Start your BRAG Sheet (30-60 minutes)
- Begin your DREAM Sheet (30-60 minutes)
- Explore the Skills Tracker

## Week 2: Intelligence Generation
- Finish BRAG and DREAM sheets
- Generate your first Career Intelligence Report
- Review report insights thoroughly
- Import skills from report to Skills Tracker
- Have your first AI Coach conversation

## Week 3: Skill Development
- Set 3-5 priority skills in Skills Tracker
- Create milestones for top priority skills
- Log your first progress updates
- Find learning resources for key skills
- Use AI Coach for development planning

## Week 4: Job Search Activation
- Upload your first job description to Job Match AI
- Review compatibility analysis
- Work through skills gap recommendations
- Practice with interview strategy coach
- Update resume based on insights

## Quick Wins

### Immediate Value (First Session)
- Complete profile to unlock dashboard features
- Upload resume for instant skill extraction
- Start AI Coach conversation for quick advice
- Check Skills Tracker sample data for ideas

### This Week
- Generate first Career Intelligence Report
- Set up 3-5 tracked skills with priorities
- Analyze one job opportunity with Job Match AI
- Have 3-4 AI Coach sessions on different topics

### This Month
- Complete all core profile elements
- Establish consistent skill tracking habit
- Analyze 5+ job opportunities
- Build interview prep materials
- Create 30-60-90 day plans`
      },
      {
        id: 'quick-wins',
        slug: 'quick-wins',
        title: 'Quick Wins & Tips',
        category: 'getting-started',
        difficulty: 'beginner',
        estimatedReadTime: 6,
        orderIndex: 3,
        description: 'Get immediate value from FutureLens Pro features',
        content: `# Quick Wins & Getting Help

## Before You Start
- **Set aside dedicated time**: Plan for 2-3 hours to thoughtfully complete everything
- **Gather materials**: Have your current resume ready
- **Reflect on your career**: Think about your biggest wins and future goals

## During Completion
- **Be thorough**: The AI can only work with the information you provide
- **Be honest**: Authentic responses lead to more relevant insights
- **Be specific**: Detailed examples yield better analysis than general statements

## After Report Generation
- **Review carefully**: Read through your entire report
- **Take action**: Implement the specific recommendations provided
- **Update regularly**: Refresh your data and generate new reports as you grow

## Getting Help
- **Dashboard Guidance**: Use the progress indicators to see what needs completion
- **Status Indicators**: Green checkmarks show completed sections
- **Support**: Contact our team if you encounter technical issues

## Common Mistakes to Avoid

### Profile Mistakes
- ❌ Incomplete profile blocks accurate AI analysis
- ✅ Spend extra time on profile - it pays dividends

### Resume Mistakes
- ❌ Scanned PDFs don't extract properly
- ✅ Use native PDF export from word processor

### BRAG/DREAM Mistakes
- ❌ Generic, brief answers limit AI insights
- ✅ Detailed, specific responses enable powerful analysis

### Skills Tracker Mistakes
- ❌ Tracking 20+ skills leads to none progressing
- ✅ Focus on 3-5 active skills with deep commitment

### AI Coach Mistakes
- ❌ Vague questions get vague answers
- ✅ Provide context and specific situation details

### Job Match AI Mistakes
- ❌ Incomplete job descriptions produce poor analysis
- ✅ Include full posting for best results`
      }
    ]
  },
  {
    id: 'profile-setup',
    name: 'Profile & Documents',
    icon: 'User',
    description: 'Complete your profile and upload essential documents',
    orderIndex: 2,
    articles: [
      {
        id: 'user-profile',
        slug: 'completing-user-profile',
        title: 'Completing Your User Profile',
        category: 'profile-setup',
        difficulty: 'beginner',
        estimatedReadTime: 10,
        orderIndex: 1,
        description: 'Set up your basic profile information for personalized insights',
        content: `# Completing Your User Profile

Your user profile is the foundation of your career analysis. Providing accurate and detailed information here helps the AI understand your background and tailor its insights.

## Getting Started
- **Accessing Your Profile**: Navigate to the "My Profile" section from the main navigation
- **Profile Information Tab**: Click on the "Profile Info" tab to access your basic information

**Tip:** Your profile data feeds into all FutureLens Pro features. Complete it first for the best experience across Skills Tracker, AI Coach, and Job Match AI.

## Required Information

### Full Name
- Enter your complete name as you would like it to appear in reports
- This helps personalize your career intelligence reports

### Years of Experience
- Provide the total number of years you've been professionally active
- Include internships and part-time work if relevant to your career
- This helps contextualize your career stage and expectations

### Preferred Role
- Describe the type of role you aspire to next
- Be specific: "Senior Software Engineer," "Marketing Manager," "Healthcare Consultant"
- Think about the level and responsibilities you want, not just the title

### Preferred Industry
- Specify the industry you wish to work in
- Examples: "Technology," "Healthcare," "Finance," "Renewable Energy," "Education"
- Consider both your current industry and industries you'd like to transition into

### Primary Motivation
- Select the option that best describes your current career circumstance
- This is crucial for the AI to align its recommendations with your personal goals:
  - **Career Growth**: You're employed and looking to advance
  - **Currently displaced/looking for a new opportunity**: You're actively job searching
  - **Looking for my second act**: You're considering a significant career change

## Saving Your Profile
- Always click "Save Profile" after making changes
- Your profile completion percentage will update automatically`
      },
      {
        id: 'resume-upload',
        slug: 'uploading-your-resume',
        title: 'Preparing and Uploading Your Resume',
        category: 'profile-setup',
        difficulty: 'beginner',
        estimatedReadTime: 12,
        orderIndex: 2,
        description: 'Learn how to properly format and upload your resume',
        content: `# Preparing and Uploading Your Resume

Your resume is a key document for the AI to understand your professional history. Proper preparation ensures accurate data extraction and analysis.

## Resume Preparation Checklist

### Format Requirements
- ✅ **PDF format only** - This is crucial for our system to process correctly
- ✅ **Text-searchable PDF** - Not just an image or scan
- ✅ **Under 10MB file size**
- ✅ **Standard fonts** - Arial, Calibri, Times New Roman work best

### Content Best Practices
- ✅ **Up-to-date information** - Include your most recent positions and achievements
- ✅ **Clear section headers** - Experience, Education, Skills, etc.
- ✅ **Quantified achievements** - Use numbers, percentages, and metrics
- ✅ **Action-oriented language** - Start bullet points with strong action verbs
- ✅ **Consistent formatting** - Use the same date format, bullet style, etc.

### What to Avoid
- ❌ Complex graphics or charts that might not extract properly
- ❌ Text in images or logos
- ❌ Unusual fonts or excessive formatting
- ❌ Password-protected PDFs
- ❌ Scanned documents (unless they're OCR-processed)

## Uploading Your Resume
1. Go to "My Profile" → "Documents" tab
2. Click "Choose Resume PDF" in the Upload Resume section
3. Select your prepared resume file
4. Wait for the upload and processing to complete
5. Verify the status shows as "Processed" with a green checkmark

## Troubleshooting Resume Upload
- If text extraction fails, check that your PDF is text-searchable
- Try exporting your resume again from your word processor
- Ensure the file isn't corrupted by opening it in a PDF viewer first

**Tip:** After uploading, you can import skills from your resume directly into Skills Tracker for instant skill tracking setup.`
      },
      {
        id: 'brag-sheet',
        slug: 'completing-brag-sheet',
        title: 'Creating Your BRAG Sheet',
        category: 'profile-setup',
        difficulty: 'intermediate',
        estimatedReadTime: 15,
        orderIndex: 3,
        description: 'Document your achievements and build your BRAG Sheet',
        content: `# Creating Your BRAG Sheet

The BRAG Sheet helps you document and articulate your professional accomplishments in detail. This becomes the foundation for your career intelligence analysis.

## What is a BRAG Sheet?

BRAG stands for your professional achievements across key dimensions:
- **B**usiness Impact
- **R**ecognition & Awards
- **A**ccomplishments
- **G**rowth & Development

## Best Practices for BRAG Responses

### Be Specific and Quantifiable
- Use specific numbers: "increased by 25%" not "significantly increased"
- Include timeframes: "within 6 months" not "eventually"
- Mention scale: "for a team of 15" or "across 3 departments"
- Show impact: revenue, cost savings, efficiency gains, user satisfaction

### Writing Tips
- Write in first person ("I led..." not "Led...")
- Use active voice and strong action verbs
- Be honest but confident in your descriptions
- Focus on your specific contributions, not just team achievements

## Completion Strategy
- **Minimum for Analysis**: Complete at least 16 out of 20 questions (80%)
- **Optimal Results**: Answer all 20 questions across all 4 sections
- **Quality over Quantity**: Better to have fewer, well-detailed responses than many brief ones
- **Save Frequently**: Use "Save Section" to preserve your work as you go

**Tip:** Your BRAG achievements will be used by AI Coach to help you craft compelling interview stories and by Job Match AI to identify your strongest talking points.

## Example Quality Response

**Question**: Describe a major project you led that had significant business impact.

**Quality Answer**:
I led the digital transformation of our customer onboarding process, which historically took 7-10 days and required manual data entry across 3 systems. I assembled a cross-functional team of 8 people (2 developers, 2 UX designers, 2 business analysts, and 2 QA specialists) and managed the project over 4 months.

We implemented an automated workflow that reduced onboarding time to 24 hours while improving data accuracy from 87% to 99%. This resulted in $340K annual cost savings through reduced manual processing and enabled us to onboard 35% more customers with the same headcount. The solution was recognized as "Innovation of the Year" at our annual company meeting and has since been adopted by 3 other business units.`
      },
      {
        id: 'dream-sheet',
        slug: 'completing-dream-sheet',
        title: 'Creating Your DREAM Sheet',
        category: 'profile-setup',
        difficulty: 'intermediate',
        estimatedReadTime: 15,
        orderIndex: 4,
        description: 'Define your career aspirations and ideal work environment',
        content: `# Creating Your DREAM Sheet

The DREAM Sheet helps you articulate your career aspirations and preferences, enabling the AI to map your ideal future path.

## Understanding the DREAM Framework

### D - Driving Motivations
- What energizes and fulfills you in your work
- The types of activities that make you lose track of time
- Your core professional values and what matters most

### R - Role Preferences
- Whether you prefer specialist or generalist roles
- Your ideal level of autonomy vs. collaboration
- The type of responsibilities that excite you

### E - Environment Fit
- Organizational culture where you thrive
- Physical and virtual work environment preferences
- Company size, mission, and values that align with you

### A - Ambitions & Aspirations
- Your ultimate career goals and vision
- Skills and experiences you want to gain
- The legacy or impact you hope to create

### M - Management Style
- How you prefer to lead and be led
- Your approach to feedback and recognition
- Your philosophy on team development and conflict resolution

## Best Practices for DREAM Responses

### Be Introspective
- Take time to really think about each question
- Consider your past experiences: when were you happiest at work?
- Think about your values and what truly motivates you

### Be Specific About Preferences
- Instead of "good work-life balance," describe what that looks like for you
- Rather than "collaborative environment," explain your ideal team dynamics
- Don't just say "growth opportunities" - specify what growth means to you

### Consider Your Whole Self
- Think about how your personal values align with work
- Consider your life stage and what you need from your career now
- Be honest about your constraints and non-negotiables

## Example Quality Response

**Question**: What type of organizational culture helps you perform at your best?

**Quality Answer**:
I thrive in cultures that balance high performance with psychological safety. I need an environment where it's safe to experiment and fail fast, where continuous learning is valued over being right all the time. I perform best when there's open communication across all levels - where junior team members can challenge senior decisions respectfully, and where feedback flows both ways. I also value cultures that prioritize sustainable work practices over constant urgency, allowing for deep work and thoughtful decision-making.

## Completion Strategy
- **Minimum for Analysis**: Complete at least 20 out of 25 questions (80%)
- **Optimal Results**: Answer all 25 questions across all 5 sections
- **Depth Matters**: Thoughtful, detailed responses yield better insights
- **Regular Reflection**: Consider revisiting and refining your answers over time

**Tip:** AI Coach uses your DREAM responses to provide personalized career guidance. The more thoughtful your answers, the better the coaching you'll receive.`
      }
    ]
  },
  {
    id: 'career-reports',
    name: 'Career Intelligence Reports',
    icon: 'FileText',
    description: 'Generate and understand your career intelligence reports',
    orderIndex: 3,
    articles: [
      {
        id: 'generating-reports',
        slug: 'generating-career-reports',
        title: 'Generating Your Career Intelligence Report',
        category: 'career-reports',
        difficulty: 'intermediate',
        estimatedReadTime: 10,
        orderIndex: 1,
        description: 'Learn how to generate comprehensive career analysis reports',
        content: `# Generating Your Career Intelligence Report

Once you've completed your profile setup, you're ready to generate your comprehensive career analysis.

## Pre-Generation Checklist

Before generating your report, ensure you have:
- ✅ **Complete Profile**: All basic information filled out
- ✅ **Resume Uploaded**: PDF successfully processed with extracted text
- ✅ **BRAG Sheet**: At least 80% complete (16+ questions answered)
- ✅ **DREAM Sheet**: At least 80% complete (20+ questions answered)

## Report Generation Process

### Initiating Generation
1. Navigate to the "Reports" section
2. Click "Generate New Report"
3. The system will create a processing session and begin analysis

### What Happens During Generation
- **Data Loading**: Your documents and responses are compiled
- **AI Analysis**: Multiple AI models analyze your information
- **Cross-Reference**: Your BRAG achievements are matched with DREAM aspirations
- **Report Assembly**: Insights are organized into a comprehensive report
- **Quality Check**: The system verifies all sections are complete

### Processing Time
- Typical generation time: 2-5 minutes
- Complex profiles may take longer
- You'll see real-time progress updates

## Understanding Your Report

### Report Sections
Your career intelligence report typically includes:
- **Executive Summary**: High-level overview of your career profile
- **Skills Analysis**: Assessment of your technical and soft skills
- **Value Proposition**: Your unique professional strengths
- **Career Opportunities**: Potential roles and career paths
- **Industry Insights**: Market analysis relevant to your goals
- **Gap Analysis**: Skills or experiences to develop
- **Strategic Recommendations**: Specific next steps for career advancement

### Using Your Report
- **Download PDF**: Save a copy for your records and job applications
- **Share Strategically**: Use insights in interviews and networking
- **Action Planning**: Focus on the specific recommendations provided
- **Regular Updates**: Generate new reports as your profile evolves

## Troubleshooting Report Generation

### If Report Generation Fails
- Check that all required documents are uploaded and processed
- Ensure your BRAG and DREAM sheets meet minimum completion requirements
- Try refreshing the page and attempting generation again
- Contact support if issues persist

### If Report Quality is Poor
- Review and enhance your BRAG and DREAM responses with more detail
- Ensure your uploaded resume contains comprehensive information
- Consider updating your profile with more specific career goals`
      }
    ]
  },
  {
    id: 'skills-tracker',
    name: 'Skills Tracker',
    icon: 'TrendingUp',
    description: 'Master your skill development journey with tracking and analytics',
    orderIndex: 4,
    articles: [
      {
        id: 'skills-tracker-intro',
        slug: 'getting-started-skills-tracker',
        title: 'Getting Started with Skills Tracker',
        category: 'skills-tracker',
        difficulty: 'beginner',
        estimatedReadTime: 12,
        orderIndex: 1,
        description: 'Introduction to the Skills Tracker system and setup',
        content: `# Getting Started with Skills Tracker

The Skills Tracker is your comprehensive system for managing skill development. Track progress, set priorities, access learning resources, and visualize your growth with powerful analytics.

## Accessing Skills Tracker

- Navigate to "Skills Tracker" from the main navigation
- Switch between "Skills" and "Analytics" tabs for different views
- Your dashboard shows current status and active skills

## Initial Setup Options

1. **Import from Report**: After generating a Career Intelligence Report, import recommended skills automatically
2. **Add Custom Skills**: Manually add skills relevant to your goals
3. **Browse Master Catalog**: Explore 25+ pre-loaded skills across categories

## Understanding Skill Categories

### Technical Skills
- Programming languages (Python, JavaScript, TypeScript, SQL, React)
- Cloud & Infrastructure (AWS, Docker, Kubernetes)
- Data & AI (Machine Learning, Data Visualization, Statistical Analysis)

### Leadership Skills
- Team Leadership, Project Management, Strategic Planning
- Change Management and organizational development

### Soft Skills
- Public Speaking, Technical Writing
- Stakeholder Management, Negotiation

### Industry-Specific Skills
- Financial Modeling, Product Management
- UX Design, Agile Methodologies, Cybersecurity, API Design

## Adding and Managing Skills

### Adding Custom Skills
1. Click "+ Add Custom Skill" button
2. Enter skill name and description
3. Select category and subcategory
4. Set initial proficiency level
5. Choose priority (1-5, where 1 is highest)
6. Save to your tracker

### Importing Skills from Resume
1. Go to My Profile → Documents
2. After resume processing, click "Import Skills"
3. Review extracted skills list
4. Select skills to add to tracker
5. Confirm import

### Setting Priorities
- **Priority 1-2**: Critical skills needed immediately (next 3 months)
- **Priority 3**: Important skills for near-term goals (3-6 months)
- **Priority 4-5**: Aspirational skills for future development (6-12 months)

**Tip:** Focus on 3-5 Priority 1-2 skills at a time. Spreading focus too thin reduces effectiveness.

## Proficiency Levels Explained

### None (0%)
- No experience with this skill
- Starting from scratch
- Need foundational learning

### Beginner (1-33%)
- Basic understanding of concepts
- Can perform simple tasks with guidance
- Building fundamental knowledge

### Intermediate (34-66%)
- Solid working knowledge
- Can complete tasks independently
- Applying skill in real projects

### Advanced (67-89%)
- Deep expertise and experience
- Can handle complex scenarios
- Mentoring others possible

### Expert (90-100%)
- Mastery of the skill
- Industry-recognized expertise
- Teaching and leading others`
      },
      {
        id: 'tracking-progress',
        slug: 'tracking-skill-progress',
        title: 'Tracking Your Progress',
        category: 'skills-tracker',
        difficulty: 'intermediate',
        estimatedReadTime: 10,
        orderIndex: 2,
        description: 'Learn how to log progress and track your skill development',
        content: `# Tracking Your Progress

## Logging Progress Updates

1. Click "Log Progress" on any active skill
2. Select update type:
   - **Progress Made**: Regular advancement
   - **Milestone Completed**: Achieved specific goal
   - **Practice Session**: Dedicated learning time
   - **Real-World Application**: Used skill in work
   - **Resource Completed**: Finished course/book
3. Enter progress percentage increase (0-100%)
4. Add time invested (hours)
5. Write notes about what you learned or accomplished
6. Save update

## Progress Update Types

### Milestone Completed
- Major achievement in skill development
- Example: "Completed AWS Solutions Architect certification"
- Marks significant capability increase

### Practice Session
- Dedicated time for learning and practice
- Example: "2 hours of React component building exercises"
- Builds consistent development habits

### Real-World Application
- Applied skill in actual project or work
- Example: "Built Docker containerization for production app"
- Demonstrates practical proficiency

### Resource Completed
- Finished learning material
- Example: "Completed 'Advanced Python' Udemy course"
- Shows structured learning progress

**Tip:** Log progress weekly for active skills. Consistent tracking reveals patterns and maintains momentum.

## Creating and Managing Milestones

### Setting Up Milestones
1. Open skill detail view
2. Click "Add Milestone"
3. Enter milestone description
4. Set target completion date
5. Define validation criteria
6. Specify required evidence (optional)
7. Save milestone

### Milestone Examples

**For Python Development**
- Complete Python for Data Science course
- Build 3 data analysis projects
- Contribute to open-source Python project
- Pass Python certification exam
- Present Python workshop to team

**For Leadership Skills**
- Lead cross-functional project team
- Complete leadership training program
- Mentor 2 junior team members
- Deliver quarterly team presentations
- Implement new team process successfully

### Milestone Best Practices
- Make milestones specific and measurable
- Set realistic timeframes (2-12 weeks typically)
- Define clear validation criteria
- Break large goals into smaller milestones
- Celebrate completions to maintain motivation`
      },
      {
        id: 'skills-analytics',
        slug: 'understanding-skills-analytics',
        title: 'Understanding Skills Analytics',
        category: 'skills-tracker',
        difficulty: 'intermediate',
        estimatedReadTime: 8,
        orderIndex: 3,
        description: 'Make data-driven decisions with skills analytics dashboard',
        content: `# Understanding Skills Analytics

## Accessing Analytics

- Click "Analytics" tab in Skills Tracker
- View comprehensive data visualizations
- Filter by time period, category, or priority

## Key Metrics Displayed

### Progress Overview
- Total skills tracked
- Active vs. completed skills
- Average progress percentage
- Overall completion rate

### Time Investment
- Total hours invested across all skills
- Hours per skill category
- Weekly/monthly time trends
- Most time-intensive skills

### Progress Velocity
- Average progress per week
- Skills with fastest advancement
- Stagnant skills (no updates 14+ days)
- Momentum indicators

### Category Distribution
- Skills by category (Technical, Leadership, Soft, Industry)
- Progress by category
- Priority distribution
- Status breakdown

### Proficiency Analysis
- Distribution across proficiency levels
- Skills approaching next level
- Readiness for advancement
- Gaps in skill portfolio

**Tip:** Review analytics monthly to identify patterns and adjust your development strategy.

## ROI-Focused Development

### Understanding Skill ROI

FutureLens Pro calculates potential return on investment for each skill across three dimensions:

**Salary Impact**
- Expected salary increase from mastering skill
- Based on market data for your role and industry
- Ranges from "Low" to "Very High"

**Opportunity Score**
- New job opportunities unlocked by skill
- Career advancement potential
- Lateral move possibilities

**Advancement Potential**
- Leadership role qualification
- Specialized positions access
- Industry mobility

### Using ROI Data for Decisions
1. Sort skills by ROI metrics
2. Prioritize high-impact skills for current goals
3. Balance quick wins with long-term investments
4. Consider your career stage and timeline

### ROI Examples

**High ROI for Mid-Career**
- Cloud Architecture (AWS/Azure): High salary impact, many opportunities
- Leadership Skills: Critical for senior positions
- Strategic Planning: Differentiator for management roles

**High ROI for Career Switchers**
- Industry-specific skills: Opens new sectors
- Modern tech stack: Brings resume up to date
- Product Management: Transition from technical roles`
      }
    ]
  },
  {
    id: 'ai-coach',
    name: 'AI Coach',
    icon: 'MessageCircle',
    description: 'Get personalized career coaching powered by AI',
    orderIndex: 5,
    articles: [
      {
        id: 'ai-coach-intro',
        slug: 'getting-started-ai-coach',
        title: 'Getting Started with AI Coach',
        category: 'ai-coach',
        difficulty: 'beginner',
        estimatedReadTime: 10,
        orderIndex: 1,
        description: 'Introduction to AI Coach and its capabilities',
        content: `# Getting Started with AI Coach

AI Coach provides personalized career guidance powered by advanced AI. Get coaching on demand, use voice conversations, and receive context-aware recommendations based on your complete profile.

## Accessing AI Coach

- Navigate to "AI Coach" from the main navigation
- View your conversation history
- Check your monthly usage quota
- Start new conversation anytime

## Your Coaching Quota

- **Free Tier**: 50 messages per month
- **Token Limit**: 50,000 tokens per month
- **Voice Enabled**: Voice input and output included
- **Resets Monthly**: Quota refreshes on the 1st of each month

**Tip:** Use AI Coach strategically for complex questions. Simple information lookups are better suited for the User Guide.

## Understanding AI Coach Capabilities

### What AI Coach Can Help With

**Career Strategy**
- Long-term career planning and goal setting
- Industry transition guidance
- Career pivot strategies
- Advancement path recommendations
- Salary negotiation preparation

**Skill Development**
- Learning roadmap creation
- Resource recommendations
- Study strategy optimization
- Overcoming learning plateaus
- Skill prioritization for goals

**Job Search & Applications**
- Resume optimization advice
- Cover letter guidance
- Application strategy
- Networking approaches
- Personal branding tips

**Interview Preparation**
- Question practice and responses
- STAR story development
- Behavioral interview strategies
- Technical interview preparation
- Salary discussion guidance

**Work Challenges**
- Navigating office politics
- Conflict resolution strategies
- Communication improvement
- Team dynamics issues
- Work-life balance optimization

**Leadership Development**
- Management skill building
- Team leadership strategies
- Delegation and empowerment
- Performance management
- Change leadership`
      },
      {
        id: 'ai-coach-voice',
        slug: 'using-voice-features',
        title: 'Using Voice Features',
        category: 'ai-coach',
        difficulty: 'intermediate',
        estimatedReadTime: 8,
        orderIndex: 2,
        description: 'Learn how to use voice input and output with AI Coach',
        content: `# Using Voice Features

## Enabling Voice Input

1. Start or open a conversation
2. Click the microphone icon in the message input area
3. Grant microphone permissions if prompted (first time only)
4. See recording indicator and timer
5. Speak clearly into your microphone
6. Click stop or wait for auto-stop (after pause)
7. Wait for transcription to complete
8. Review transcribed text before sending
9. Edit if needed, then send

## Voice Input Best Practices

- Speak in quiet environment for best accuracy
- Speak at natural pace, not too fast or slow
- Enunciate clearly, especially technical terms
- Pause briefly between separate thoughts
- Use complete sentences for better transcription
- Review text before sending for accuracy

## Enabling Voice Output

1. Click the speaker icon to toggle voice responses
2. When enabled (blue icon), AI responses will be spoken
3. Choose from 6 available voices:
   - **Alloy**: Balanced, neutral tone
   - **Echo**: Clear, professional sound
   - **Fable**: Warm, engaging delivery
   - **Onyx**: Deep, authoritative voice
   - **Nova**: Energetic, motivating tone
   - **Shimmer**: Friendly, approachable style
4. Adjust playback speed (0.25x to 4x)
5. Voice plays automatically when response completes

## Voice Output Tips

- Use headphones in shared spaces
- Adjust speed for comfort (1.2x is popular)
- Pause/resume playback as needed
- Can read transcript while listening
- Voice output counts toward voice minutes quota

## Technical Requirements

- Modern browser (Chrome, Firefox, Safari, Edge)
- Microphone access (will request permission)
- Stable internet connection
- Audio output capability (speakers/headphones)

## Supported Audio Formats

- Input: WebM (Opus), WebM, MP4, WAV, OGG
- Output: MP3 (streamed from server)`
      },
      {
        id: 'effective-prompting',
        slug: 'effective-prompting-strategies',
        title: 'Effective Prompting Strategies',
        category: 'ai-coach',
        difficulty: 'intermediate',
        estimatedReadTime: 12,
        orderIndex: 3,
        description: 'Learn how to ask better questions and get better responses',
        content: `# Effective Prompting Strategies

## Getting Better Responses

### Be Specific
- ❌ Bad: "Help me with my career"
- ✅ Good: "I want to transition from software engineering to product management in the next 12 months. What skills should I prioritize?"

### Provide Context
- ❌ Bad: "Should I take this job?"
- ✅ Good: "I have an offer for a senior engineer role at a startup. It's 15% more salary but less stability than my current corporate job. I value growth opportunities over security. What factors should I consider?"

### Ask Follow-Up Questions
- Dig deeper into recommendations
- Request examples or resources
- Ask about alternative approaches
- Clarify anything unclear

### Be Honest About Constraints
- Time availability for learning
- Financial limitations
- Family or personal commitments
- Current energy and motivation levels
- Risk tolerance

### Request Specific Formats
- "Give me 3 specific action items"
- "Create a 30-60-90 day plan"
- "List pros and cons in a table"
- "Provide step-by-step instructions"
- "Suggest 5 resources to learn this"

## Context-Aware Coaching

### How AI Coach Uses Your Data

AI Coach has access to your complete FutureLens Pro profile to provide personalized guidance:

**Profile Information**
- Years of experience and career stage
- Preferred role and industry goals
- Primary motivation and situation
- Education and background

**Resume Data**
- Work history and positions
- Skills and technologies
- Education and certifications
- Achievements and responsibilities

**BRAG Sheet**
- Quantified accomplishments
- Project successes
- Impact demonstrations
- Leadership examples

**DREAM Sheet**
- Career aspirations and goals
- Work preferences and values
- Motivations and priorities
- Environmental preferences

**Skills Tracker**
- Current skill levels and progress
- Active learning initiatives
- Development priorities
- Progress velocity and patterns

**Job Match Analyses**
- Target role requirements
- Compatibility scores
- Identified skill gaps
- Interview strategies prepared

## Example Context Usage

**Without Context:**
"Focus on leadership skills to get promoted."

**With Context:**
"Based on your 5 years of experience and your goal to move into a senior engineering role at a tech company, I recommend focusing on three specific areas: (1) Technical leadership through architecture review participation, (2) Mentoring 1-2 junior engineers (you mentioned enjoying teaching), and (3) Driving one cross-team initiative in the next quarter. These align with your strengths in system design and collaboration while addressing the leadership gap identified in your recent job match analysis for senior positions."`
      }
    ]
  },
  {
    id: 'job-match-ai',
    name: 'Job Match AI',
    icon: 'Briefcase',
    description: 'Analyze job opportunities and get interview strategies',
    orderIndex: 6,
    articles: [
      {
        id: 'job-match-intro',
        slug: 'getting-started-job-match',
        title: 'Getting Started with Job Match AI',
        category: 'job-match-ai',
        difficulty: 'beginner',
        estimatedReadTime: 10,
        orderIndex: 1,
        description: 'Introduction to Job Match AI and prerequisites',
        content: `# Getting Started with Job Match AI

Job Match AI analyzes job opportunities against your complete profile, providing detailed compatibility scores, interview strategies, and skill development recommendations.

## Accessing Job Match AI

- Navigate to "Job Match AI" from main navigation
- Ensure prerequisites are complete (see below)
- View your match history
- Upload new job descriptions

## Prerequisites for Job Match AI

Before using Job Match AI effectively, complete:
- ✅ User profile with experience and goals
- ✅ Resume uploaded and processed
- ✅ BRAG Sheet at least 80% complete
- ✅ DREAM Sheet at least 80% complete
- ✅ Career Intelligence Report generated (recommended)

## Why Prerequisites Matter

Job Match AI analyzes compatibility by comparing:
- Job requirements → Your resume skills and experience
- Company culture indicators → Your DREAM preferences
- Required achievements → Your BRAG accomplishments
- Growth opportunities → Your career goals

Incomplete data results in lower-quality analysis and less actionable recommendations.

**Tip:** Generate a Career Intelligence Report before using Job Match AI. The report provides AI-analyzed skill baselines that improve match accuracy.

## Understanding the Six-Tab Interface

### Tab 1: Upload Job
- Add new job descriptions for analysis
- Input via PDF upload or text paste
- Enter job title and company name
- Submit for AI processing

### Tab 2: History
- View all past job matches
- See match scores at a glance
- Filter by date, company, or score
- Select any match to view details

### Tab 3: Analysis
- Comprehensive compatibility assessment
- Overall match score and breakdown
- Strengths and weaknesses
- Key talking points for interviews
- Detailed alignment analysis

### Tab 4: Skills Gap
- Specific skills to develop
- Current level vs. required level
- ROI-focused development plans
- Recommended learning resources
- Timeline estimates for skill acquisition

### Tab 5: Interview Strategy
- Executive-level interview coaching
- Strategic talking points
- Weakness response strategies
- Questions to ask employer
- 30-60-90 day plan framework
- STAR story recommendations

### Tab 6: General Prep
- General interview guidance
- Common questions and approaches
- Interview format preparation
- Follow-up best practices
- Offer negotiation tips`
      },
      {
        id: 'uploading-jobs',
        slug: 'uploading-job-descriptions',
        title: 'Uploading Job Descriptions',
        category: 'job-match-ai',
        difficulty: 'beginner',
        estimatedReadTime: 8,
        orderIndex: 2,
        description: 'Learn how to upload job descriptions for analysis',
        content: `# Uploading Job Descriptions

## Method 1: PDF Upload

1. Click "Upload Job" tab
2. Click "Choose File" or drag-and-drop PDF
3. PDF should contain complete job posting
4. Wait for text extraction
5. Review extracted content
6. Add job title and company name
7. Click "Analyze Job Match"

## Method 2: Text Paste

1. Copy job description from website or email
2. Click "Upload Job" tab
3. Paste into text area
4. Include full description, requirements, and qualifications
5. Add job title and company name
6. Click "Analyze Job Match"

## What to Include

### Essential Information
- Complete job description and responsibilities
- Required qualifications and skills
- Preferred qualifications
- Education requirements
- Experience requirements
- Technical skills and tools mentioned
- Soft skills and competencies
- Company culture indicators
- Team structure information
- Growth opportunities mentioned

### Optional but Helpful
- Salary range if provided
- Benefits and perks
- Location and remote policy
- Company mission and values
- Recent company news or initiatives
- Interview process description

**Tip:** More complete job descriptions yield better analysis. Include everything from the original posting.

## Processing Time

- Analysis typically takes 30-90 seconds
- You'll see real-time progress indicators
- Multiple AI personas analyze different aspects
- Results appear in Analysis tab when complete`
      },
      {
        id: 'understanding-match-scores',
        slug: 'understanding-match-analysis',
        title: 'Understanding Match Analysis',
        category: 'job-match-ai',
        difficulty: 'intermediate',
        estimatedReadTime: 12,
        orderIndex: 3,
        description: 'Learn how to interpret compatibility scores and recommendations',
        content: `# Understanding Match Analysis

## Overall Match Score

Your match score (0-100%) indicates overall compatibility:
- **90-100%**: Exceptional fit, strong candidate
- **75-89%**: Very good match, competitive candidate
- **60-74%**: Good fit with some development needed
- **45-59%**: Moderate fit, significant gaps to address
- **Below 45%**: Substantial gaps, major stretch role

### Match score is calculated from:
- Skills alignment (technical and soft skills)
- Experience level match
- Industry background fit
- Cultural compatibility (DREAM vs. job indicators)
- Achievement relevance (BRAG vs. requirements)
- Career trajectory alignment

**Tip:** Scores below 60% don't mean "don't apply." They highlight areas to address in your application and interview prep.

## Strengths Analysis

### What You'll See
- Specific skills you possess that match requirements
- Relevant experience and achievements
- Cultural alignment factors
- Unique differentiators you bring
- Standout qualifications

### How to Use
- Lead with these in your cover letter
- Emphasize these strengths early in interviews
- Provide detailed examples from BRAG sheet
- Align with resume bullet points
- Use in LinkedIn/networking conversations

### Example Strengths Output
"Strong Python and data analysis skills directly match requirements. Your experience leading cross-functional teams (15 people) exceeds the job's requirement for team collaboration. Your achievement of 40% efficiency improvement demonstrates the data-driven impact this role seeks."

## Weaknesses Analysis

### What You'll See
- Required skills you lack or need to strengthen
- Experience gaps (e.g., industry, company size)
- Certifications or education requirements not met
- Tool or technology mismatches
- Cultural fit concerns

### How to Use
- Create action plan to address before applying
- Prepare honest responses about gaps
- Identify transferable skills for substitution
- Use Skills Tracker to close critical gaps
- Frame gaps as growth opportunities in interviews

### Example Weaknesses Output
"Limited experience with Kubernetes (required skill). No direct FinTech industry background. Lack of formal project management certification mentioned as preferred qualification."

## Talking Points

### Strategic Messages
- Key points to emphasize throughout application and interviews
- Connections between your experience and role requirements
- Stories from BRAG sheet matched to job needs
- Unique value propositions
- Proof points for key qualifications

### How to Use
- Incorporate into cover letter
- Prepare as interview responses
- Use in networking conversations
- Reference in thank-you notes
- Include in LinkedIn profile updates

### Example Talking Points
1. "Led digital transformation initiative that increased productivity by 35% - directly relevant to the company's modernization goals"
2. "5 years of Python development plus machine learning certifications demonstrate the technical depth required"
3. "Experience managing globally distributed teams aligns with the role's remote-first environment"`
      },
      {
        id: 'interview-strategy',
        slug: 'executive-interview-strategy',
        title: 'Executive Interview Strategy',
        category: 'job-match-ai',
        difficulty: 'advanced',
        estimatedReadTime: 15,
        orderIndex: 4,
        description: 'Get sophisticated interview preparation and strategies',
        content: `# Executive Interview Strategy

## What This Provides

The Executive Interview Coach persona delivers sophisticated, executive-level interview preparation:

### Strategic Talking Points
- High-level themes to emphasize
- Leadership narratives from your background
- Value proposition statements
- Vision and strategic thinking demonstrations

### Weakness Response Strategies
- Honest yet positive framing of gaps
- Transferable skills to emphasize instead
- Growth mindset communication
- Specific plans to close gaps

### Questions to Ask Employer
- Strategic questions about company direction
- Questions revealing culture and values
- Inquiries showing business acumen
- Team dynamics and support questions
- Growth and development opportunities

### 30-60-90 Day Plan
- First 30 days: Learning, listening, relationship building
- Days 31-60: Quick wins, establish credibility
- Days 61-90: Strategic initiatives launched
- Customized to role and company context

### STAR Stories
- Situation-Task-Action-Result format
- Stories from BRAG sheet matched to likely questions
- Leadership examples
- Problem-solving demonstrations
- Quantified impact stories

### Company Culture Alignment
- How to demonstrate fit with culture cues from posting
- Referencing company values and mission
- Showing alignment with work style
- Cultural intelligence display

**Tip:** The Interview Strategy is comprehensive (often 2-3 pages). Review thoroughly 2-3 days before interview, practice key points aloud, and take notes into the interview.

## Using the Strategy Effectively

### One Week Before
- Read complete strategy document
- Identify 5-7 key talking points to memorize
- Prepare your STAR stories in detail
- Research company beyond job posting
- Practice 30-60-90 day plan presentation

### Three Days Before
- Practice responses aloud
- Refine weakness response strategies
- Finalize questions to ask
- Prepare specific examples for each talking point
- Do mock interview with friend or AI Coach

### Day Before
- Review key talking points
- Rehearse opening introduction (2-minute pitch)
- Print or save strategy for reference
- Prepare questions list to bring
- Get good sleep

### Day Of
- Quick review of main themes (don't cram)
- Review 30-60-90 day plan
- Have questions list accessible
- Bring portfolio or examples if relevant
- Stay confident - you're prepared`
      }
    ]
  },
  {
    id: 'tips-tricks',
    name: 'Tips & Best Practices',
    icon: 'Lightbulb',
    description: 'Power user tips and expert strategies',
    orderIndex: 7,
    articles: [
      {
        id: 'power-user-shortcuts',
        slug: 'power-user-shortcuts',
        title: 'Power User Shortcuts',
        category: 'tips-tricks',
        difficulty: 'advanced',
        estimatedReadTime: 8,
        orderIndex: 1,
        description: 'Efficiency tips for experienced users',
        content: `# Power User Shortcuts

## Dashboard Efficiency
- Use dashboard as your career command center
- Check completion percentages before generating reports
- Monitor AI Coach quota to plan coaching sessions
- Review Skills Tracker summary for quick status

## Quick Navigation
- Bookmark frequently used pages
- Use browser back button to return to previous feature
- Open multiple tabs for cross-referencing
- Keep Job Match AI history open while updating Skills Tracker

## Data Management
- Save important AI Coach conversations for reference
- Export Career Intelligence Reports to PDF immediately
- Take screenshots of Job Match AI analysis for applications
- Keep development notes in Skills Tracker entries

## Batch Operations
- Analyze 5-10 jobs at once for pattern recognition
- Update multiple skills simultaneously in tracker
- Schedule multiple AI Coach topics for one session
- Generate all application materials in single sitting

## Time-Saving Strategies

### Resume Optimization
- Import skills to avoid manual entry
- Use Job Match talking points for bullet points
- Update once, use analysis to customize for each job
- Keep master version with all accomplishments

### Skills Tracking
- Set weekly reminder for progress logging
- Use voice-to-text for quick notes
- Batch-update multiple skills at once
- Focus deeply on 3-5 skills max

### AI Coach Efficiency
- Ask compound questions to save messages
- Request numbered action lists
- Use follow-ups to dig deeper without new context
- Save frequently asked question responses

### Job Match AI Speed
- Keep clipboard with resume text for paste method
- Create template for company research notes
- Save generic interview prep once, customize per job
- Build reusable STAR story library`
      },
      {
        id: 'integrated-workflows',
        slug: 'integrated-workflows',
        title: 'Integrated Workflows',
        category: 'tips-tricks',
        difficulty: 'advanced',
        estimatedReadTime: 15,
        orderIndex: 2,
        description: 'How to use multiple features together effectively',
        content: `# Integrated Workflows

## Workflow 1: Complete Career Foundation

**Goal**: Establish comprehensive career profile

**Steps**:
1. Complete user profile (10 minutes)
2. Upload resume and verify extraction (5 minutes)
3. Complete BRAG Sheet (1-2 hours)
4. Complete DREAM Sheet (1-2 hours)
5. Generate Career Intelligence Report (5 minutes + wait)
6. Review report thoroughly (30 minutes)
7. Import recommended skills to Skills Tracker
8. Set initial priorities on 5-7 skills
9. Have introductory AI Coach conversation
10. Set weekly calendar blocks for skill development

**Time Investment**: 4-6 hours
**Outcome**: Complete foundation for all other features
**Next Steps**: Begin skill development and job exploration

## Workflow 2: Targeted Job Search

**Goal**: Land specific role within 90 days

**Steps**:
1. Ensure career foundation complete (see Workflow 1)
2. Identify 5-10 target companies and roles
3. Collect job descriptions for all targets
4. Analyze all jobs with Job Match AI
5. Review match scores and prioritize top 5
6. Identify common skill gaps across targets
7. Add critical skill gaps to Skills Tracker as Priority 1
8. Begin skill development immediately
9. Prepare interview strategy for top 3 matches
10. Consult AI Coach for application strategy
11. Customize resume using Job Match talking points
12. Apply to top matches within 1 week
13. Continue skill development during application process
14. Use AI Coach for interview practice
15. Track all applications and follow-ups

**Time Investment**:
- Initial setup: 8-10 hours
- Ongoing: 10-15 hours/week for 90 days

**Outcome**: 5+ quality applications, improved skills, strong interview performance
**Success Metrics**: Applications submitted, interviews secured, offers received

## Workflow 3: Career Transition Preparation

**Goal**: Prepare for career change over 6-12 months

**Steps**:
1. Complete career foundation thoroughly
2. Use AI Coach to explore transition strategy
3. Research target roles (analyze 10-15 with Job Match AI)
4. Identify skill gap patterns across target roles
5. Create comprehensive skills roadmap in Skills Tracker
6. Set realistic timeline based on gap sizes
7. Prioritize foundational skills first
8. Dedicate consistent time weekly (10+ hours)
9. Build portfolio projects demonstrating new skills
10. Update resume quarterly with new competencies
11. Network in target industry throughout
12. Re-analyze same jobs monthly to track progress
13. Use AI Coach for motivation and strategy adjustments
14. Begin targeted applications when match scores reach 60%+
15. Continue development while interviewing

**Time Investment**:
- Planning: 10-15 hours
- Execution: 10-20 hours/week for 6-12 months

**Outcome**: New skill set, credible transition story, portfolio of work
**Success Metrics**: Match scores increasing, network growing, interviews in new field

## Workflow 4: Continuous Career Development

**Goal**: Maintain competitive edge and career momentum

**Weekly** (2-3 hours)
- Log skill progress updates for active skills
- Spend 2+ hours on skill development
- Check job market (analyze 1-2 relevant jobs)
- Review and update priorities as needed

**Monthly** (3-4 hours)
- Update BRAG Sheet with recent achievements
- Review Skills Tracker analytics
- Have AI Coach strategy conversation
- Analyze 3-5 interesting job postings
- Update resume if significant changes
- Set goals for next month

**Quarterly** (6-8 hours)
- Generate new Career Intelligence Report
- Comprehensive DREAM Sheet review and updates
- Major skills roadmap adjustment
- Market research (analyze 10+ roles)
- Network review and expansion
- LinkedIn profile update
- Career goals review and adjustment

**Annually** (12-15 hours)
- Complete career assessment
- Major resume overhaul
- Comprehensive job market analysis
- Strategic career planning session with AI Coach
- Skills portfolio and personal brand audit
- Professional development budget planning
- Mentor/coaching relationships evaluation

**Time Investment**: 120-180 hours/year
**Outcome**: Consistent growth, market awareness, career optionality
**Success Metrics**: Skills advancing, opportunities increasing, satisfaction rising`
      }
    ]
  },
  {
    id: 'video-tutorials',
    name: 'Video Tutorials',
    icon: 'Video',
    description: 'Step-by-step video guides to help you complete your BRAG and DREAM sheets',
    orderIndex: 100,
    articles: [
      {
        id: 'video-brag-overview',
        slug: 'video-brag-sheet-overview',
        title: 'BRAG Sheet Overview & Getting Started',
        category: 'video-tutorials',
        difficulty: 'beginner',
        estimatedReadTime: 5,
        orderIndex: 1,
        description: 'Learn what a BRAG sheet is and how to identify your professional achievements',
        content: `# BRAG Sheet Overview & Getting Started

## About This Video

This 4-minute tutorial introduces the BRAG framework and helps you understand how to identify and articulate your professional achievements effectively.

## Video Content

**What is covered:**
- Understanding the BRAG acronym
- Why documenting achievements matters for your career
- The psychology of remembering your accomplishments
- Strategies for recalling forgotten achievements
- Examples of strong BRAG responses

## Accessing the Video

You can watch this tutorial:
- In the BRAG sheet itself (look for the "Need Help?" panel)
- Right here in the User Guide
- From your profile page before starting

## Key Takeaways

1. **B = Breakthrough**: Focus on challenges you overcame
2. **R = Remarkable**: Highlight your most impressive achievements
3. **A = Actionable**: Show practical applications of your skills
4. **G = Growth**: Demonstrate your professional evolution

## Tips for Success

- Set aside 30-60 minutes for thoughtful completion
- Be specific with examples and metrics
- Focus on impact, not just activities
- Use the STAR method (Situation, Task, Action, Result)

## Next Steps

After watching this overview:
1. Watch section-specific tutorials as needed
2. Complete each BRAG section with detailed responses
3. Review and refine your answers before saving`
      },
      {
        id: 'video-brag-breakthrough',
        slug: 'video-breakthrough-achievements',
        title: 'Breakthrough Achievements: Overcoming Challenges',
        category: 'video-tutorials',
        difficulty: 'beginner',
        estimatedReadTime: 4,
        orderIndex: 2,
        description: 'How to recall and articulate times you overcame significant obstacles',
        content: `# Breakthrough Achievements: Overcoming Challenges

## About This Video

This tutorial focuses specifically on the "B" in BRAG - identifying and describing times when you overcame significant professional challenges.

## Key Focus Areas

- Recognizing what counts as a "breakthrough"
- Frameworks for describing obstacles
- Articulating your problem-solving process
- Quantifying the impact of overcoming challenges
- Examples from various industries and roles

## Common Breakthrough Scenarios

1. **Technical Challenges**: Solving complex problems
2. **Resource Constraints**: Achieving more with less
3. **Time Pressure**: Delivering under tight deadlines
4. **Organizational Change**: Leading through transitions
5. **Conflict Resolution**: Navigating difficult relationships

## Tips for This Section

- Think about times you felt stuck, then found a solution
- Consider obstacles that others couldn't overcome
- Focus on situations that pushed you outside your comfort zone
- Include the "before and after" state

## Writing Formula

**Situation**: Describe the challenge
**Obstacle**: What made it difficult?
**Action**: What did you do specifically?
**Result**: What was the measurable outcome?
**Learning**: What did you gain from the experience?`
      },
      {
        id: 'video-dream-overview',
        slug: 'video-dream-sheet-overview',
        title: 'DREAM Sheet Overview & Career Vision',
        category: 'video-tutorials',
        difficulty: 'beginner',
        estimatedReadTime: 6,
        orderIndex: 3,
        description: 'Understanding your career motivations, preferences, and aspirations',
        content: `# DREAM Sheet Overview & Career Vision

## About This Video

This comprehensive 6-minute tutorial covers the entire DREAM framework and helps you articulate your career vision, preferences, and goals.

## Video Content

**What is covered:**
- Understanding the DREAM acronym
- Why career clarity matters for success
- How to identify your true motivations
- Aligning your preferences with opportunities
- Setting meaningful career aspirations

## The DREAM Framework

1. **D = Driving Motivations**: What energizes you at work
2. **R = Role Preferences**: Your ideal work structure
3. **E = Environment Fit**: Where you thrive
4. **A = Ambitions**: Your career goals and vision
5. **M = Management Style**: How you lead and like to be led

## Why This Matters

Your DREAM sheet helps:
- Identify roles that truly fit your values
- Avoid career mismatches
- Communicate your preferences to employers
- Guide strategic career decisions
- Achieve long-term satisfaction

## Reflection Questions

Before starting, consider:
- What work makes you lose track of time?
- What accomplishments make you proudest?
- What type of environment helps you excel?
- Where do you see yourself in 5-10 years?
- What leadership style brings out your best?

## Tips for Authenticity

- Be honest, even if answers seem unconventional
- Don't write what you think employers want to hear
- Consider your actual experiences, not ideals
- Reflect on patterns across your career
- Think about what drains vs. energizes you

## Using Your Responses

Your DREAM sheet will:
- Guide job search targeting
- Inform interview conversations
- Shape networking strategy
- Drive development planning
- Enable better career decisions`
      }
    ]
  }
];
